import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare, Bot, Users, Flame, Send, Zap, BarChart3, ShieldCheck,
  Workflow, Megaphone, Check, ArrowRight, Sparkles, CheckCheck, UserCheck, Globe
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const PRICING = [
  { name: "Starter", price: "99", popular: false, tagKey: "tag_entry",
    features: ["1 WhatsApp Number", "2 Team Members", "Shared Team Inbox", "Basic CRM & Tags", "Basic Automation", "Basic Reports"] },
  { name: "Growth", price: "299", popular: true, tagKey: "tag_popular",
    features: ["1 WhatsApp Number", "5 Team Members", "AI Chatbot + Knowledge Base", "Lead Qualification", "Approved Template Manager", "Broadcast + Segmentation", "Sales Pipeline"] },
  { name: "Pro", price: "599", popular: false, tagKey: "tag_scaling",
    features: ["Up to 2 WhatsApp Numbers", "15 Team Members", "AI Sales Agent + Lead Scoring", "Smart Routing + AI Summary", "Campaign Analytics", "Ads Attribution", "Revenue Tracking"] },
  { name: "Business", price: "999", popular: false, tagKey: "tag_multibranch",
    features: ["Up to 5 WhatsApp Numbers", "30 Team Members", "Multi-Branch + Supervisor", "Advanced Automation + SLA", "Management AI", "TravelOS / StoreOS", "Higher API Limits"] },
];

const FEATURES = [
  { icon: Bot, tk: "f_chatbot_t", dk: "f_chatbot_d" },
  { icon: MessageSquare, tk: "f_inbox_t", dk: "f_inbox_d" },
  { icon: UserCheck, tk: "f_takeover_t", dk: "f_takeover_d" },
  { icon: Flame, tk: "f_score_t", dk: "f_score_d" },
  { icon: Megaphone, tk: "f_broadcast_t", dk: "f_broadcast_d" },
  { icon: Workflow, tk: "f_automation_t", dk: "f_automation_d" },
  { icon: Users, tk: "f_crm_t", dk: "f_crm_d" },
  { icon: BarChart3, tk: "f_analytics_t", dk: "f_analytics_d" },
];

export default function Landing() {
  const { t, toggle, lang } = useLang();
  const [demoStep, setDemoStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setDemoStep((s) => (s + 1) % 4), 2200);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 dark:bg-slate-900/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="nav-brand-logo">
            <div className="relative h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-primary-foreground" />
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-400 border-2 border-white dark:border-slate-900" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">WasapMe</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors" data-testid="nav-link-features">{t("nav_features")}</a>
            <a href="#solutions" className="hover:text-foreground transition-colors" data-testid="nav-link-solutions">{t("nav_solutions")}</a>
            <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-link-pricing">{t("nav_pricing")}</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggle} data-testid="lang-toggle-button"
              className="flex items-center gap-1.5 text-sm font-medium px-2.5 py-1.5 rounded-lg hover:bg-secondary transition-colors">
              <Globe className="h-4 w-4" /> {t("lang_toggle")}
            </button>
            <Button variant="ghost" onClick={startLogin} data-testid="nav-login-button" className="hidden sm:inline-flex">{t("nav_login")}</Button>
            <Button onClick={startLogin} data-testid="button-start-free-trial" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full">
              {t("nav_trial")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7">
            <Badge className="bg-accent text-accent-foreground border-0 mb-5 rounded-full px-3 py-1">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {t("hero_badge")}
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]" data-testid="hero-title">
              {t("hero_title").split(lang === "ms" ? "AI" : "AI Sales Team")[0]}
              <span className="text-primary">{lang === "ms" ? "AI" : "AI Sales Team"}</span>
              {t("hero_title").split(lang === "ms" ? "AI" : "AI Sales Team")[1]}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={startLogin} data-testid="hero-cta-trial"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full text-base h-12 px-7">
                {t("hero_cta1")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={startLogin} data-testid="hero-cta-demo"
                className="rounded-full text-base h-12 px-7">
                {t("hero_cta2")}
              </Button>
              <span className="text-sm font-semibold text-primary ml-1">{t("hero_from")}</span>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" /> {t("hero_nocard")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" /> {t("hero_setup")}</span>
            </div>
          </motion.div>

          {/* Live chat simulator */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5">
            <div className="relative rounded-3xl border border-border bg-card shadow-2xl overflow-hidden">
              <div className="bg-primary px-4 py-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold text-sm">Aida AI · WasapMe</p>
                  <p className="text-white/70 text-xs flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-300" /> Online 24/7</p>
                </div>
                <MessageSquare className="h-5 w-5 text-white/80" />
              </div>
              <div className="chat-wallpaper p-4 space-y-3 min-h-[340px]">
                <ChatBubble side="in" show={demoStep >= 0}>Salam, Umrah Disember ada lagi? Berapa harga?</ChatBubble>
                <ChatBubble side="out" ai show={demoStep >= 1}>
                  Waalaikumsalam! 😊 Ya ada. Pakej Umrah Disember bermula RM8,990 (flight + hotel 4⭐ + visa). Untuk berapa orang ya?
                </ChatBubble>
                {demoStep >= 2 && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="flex justify-center">
                    <Badge className="bg-rose-100 text-rose-700 border-0 rounded-full">
                      <Flame className="h-3.5 w-3.5 mr-1" /> Lead Score +15 → HOT 92/100
                    </Badge>
                  </motion.div>
                )}
                <ChatBubble side="in" show={demoStep >= 3}>Untuk 2 orang. Ada ansuran?</ChatBubble>
                {demoStep >= 3 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="flex justify-center pt-1">
                    <Badge variant="outline" className="rounded-full text-primary border-primary/40">
                      <UserCheck className="h-3.5 w-3.5 mr-1" /> Transfer to Salesperson →
                    </Badge>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Flow strip */}
      <section id="solutions" className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-primary mb-6">{t("convert_label")}</p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm font-semibold">
            {["WHATSAPP", "AI", "CRM", "STAFF", "FOLLOW-UP", "SALE", "REVENUE"].map((s, i) => (
              <React.Fragment key={s}>
                <span className="px-3 py-1.5 rounded-full bg-card border border-border">{s}</span>
                {i < 6 && <ArrowRight className="h-4 w-4 text-primary" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">{t("features_eyebrow")}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("features_title")}</h2>
          <p className="mt-4 text-muted-foreground">{t("features_sub")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.tk} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="group p-6 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-lg transition-all">
              <div className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-base mb-1.5">{t(f.tk)}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t(f.dk)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">{t("pricing_eyebrow")}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("pricing_title")}</h2>
            <p className="mt-4 text-muted-foreground">{t("pricing_sub")}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING.map((p) => (
              <div key={p.name} data-testid={`pricing-card-${p.name.toLowerCase()}`}
                className={`relative rounded-2xl border bg-card p-6 flex flex-col ${p.popular ? "border-primary ring-2 ring-primary shadow-xl lg:scale-105" : "border-border"}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    {t("tag_popular")}
                  </span>
                )}
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(p.tagKey)}</p>
                <h3 className="font-display text-xl font-bold mt-1">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold font-display">RM{p.price}</span>
                  <span className="text-muted-foreground text-sm">{t("per_month")}</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={startLogin} data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  className={`mt-6 rounded-full w-full ${p.popular ? "bg-primary hover:bg-primary/90 text-primary-foreground" : ""}`}
                  variant={p.popular ? "default" : "outline"}>
                  {t("start_now")}
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-8 max-w-3xl mx-auto rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-4 text-sm text-amber-800 dark:text-amber-300 flex gap-3">
            <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" />
            <p>
              <span className="font-semibold">{t("pricing_note_label")}</span> {t("pricing_note")} <span className="font-semibold">{t("pricing_note_bold")}</span> {t("pricing_note_end")}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="relative rounded-3xl bg-slate-900 dark:bg-slate-950 p-10 sm:p-16 text-center overflow-hidden">
          <div className="grain-overlay" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">{t("cta_title")}</h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">CHAT • AI • CRM • BROADCAST • AUTOMATION • SALES • REVENUE</p>
            <Button size="lg" onClick={startLogin} data-testid="footer-cta-button"
              className="mt-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-8 text-base">
              {t("hero_cta1")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-primary flex items-center justify-center">
              <MessageSquare className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-display font-bold text-foreground">WasapMe</span>
          </div>
          <p>© 2026 WasapMe. WhatsApp is a trademark of Meta Platforms, Inc.</p>
        </div>
      </footer>
    </div>
  );
}

function ChatBubble({ side, ai, children, show }) {
  if (!show) return null;
  const isIn = side === "in";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`flex ${isIn ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${
        isIn ? "bg-white text-slate-800 rounded-tl-sm" : "bg-[#DCF8C6] text-slate-800 rounded-tr-sm"}`}>
        {ai && <span className="flex items-center gap-1 text-[10px] font-semibold text-primary mb-1"><Bot className="h-3 w-3" /> AI</span>}
        {children}
        {!isIn && <CheckCheck className="inline-block h-3.5 w-3.5 ml-1 text-sky-500" />}
      </div>
    </motion.div>
  );
}
